import {
  InvestorViewerPerson,
  InvestorViewerData,
  ViewerAddress,
  ViewerAssetHolder,
  ViewerCoupleRelation,
  ViewerFamilyRelation,
  ViewerLiability,
  ViewerLiabilityHolder,
  ViewerNote,
  ViewerOtherRelation,
  ViewerPhone,
  ViewerRevenueCharge,
  ViewerTax
} from '../../../../core/models/api.models';

export type AdditionalField = {
  label: string;
  value: string;
};

const numberFormatter = new Intl.NumberFormat('fr-FR', {
  minimumFractionDigits: 0,
  maximumFractionDigits: 2
});

export function formatText(value: string | number | null | undefined): string {
  if (value === null || value === undefined) {
    return '-';
  }

  const text = value.toString().trim();
  return text ? text : '-';
}

export function formatBoolean(value: boolean | null | undefined): string {
  if (value === null || value === undefined) {
    return '-';
  }

  return value ? 'Oui' : 'Non';
}

export function formatNumber(value: number | null | undefined): string {
  if (value === null || value === undefined) {
    return '-';
  }

  return numberFormatter.format(value);
}

export function formatCurrency(value: number | null | undefined): string {
  if (value === null || value === undefined) {
    return '-';
  }

  return `${numberFormatter.format(value)} €`;
}

export function formatPercentage(value: number | null | undefined): string {
  if (value === null || value === undefined) {
    return '-';
  }

  return `${numberFormatter.format(value)} %`;
}

export function formatDate(value: string | null | undefined): string {
  if (!value?.trim()) {
    return '-';
  }

  return value
    .replace('T00:00:00.0000000', '')
    .replace('T00:00:00', '')
    .replace('T', ' ');
}

export function formatNotes(notes: ViewerNote[]): string {
  const values = notes
    .map(note => note.note?.trim())
    .filter((note): note is string => !!note);

  return values.length > 0 ? values.join(' • ') : '-';
}

export function formatIdentity(
  lastName?: string | null,
  firstName?: string | null,
  fallback?: string
): string {
  const label = [lastName, firstName]
    .map(value => value?.trim())
    .filter((value): value is string => !!value)
    .join(' ');

  return label || fallback || '-';
}

export function formatPersonLabel(person: InvestorViewerPerson): string {
  const label = formatIdentity(person.lastName, person.firstName, `Personne #${person.personId}`);
  return `${label} #${person.personId}`;
}

export function formatInvestorLink(investorId?: number | null): string {
  return investorId ? `Investisseur #${investorId}` : '-';
}

export function formatHolderLabel(investorId?: number | null): string {
  return investorId ? `Investisseur #${investorId}` : 'Détenteur non renseigné';
}

export function formatAddressLines(...values: Array<string | null | undefined>): string {
  const parts = values
    .map(value => value?.trim())
    .filter((value): value is string => !!value);

  return parts.length > 0 ? parts.join(' • ') : '-';
}

export function formatAddress(address: ViewerAddress): string {
  return formatAddressLines(
    address.address1,
    address.address2,
    address.address3,
    address.complement,
    joinWithSeparator(' ', address.zipCode, address.city),
    address.cedex
  );
}

export function formatPhone(phone: ViewerPhone): string {
  return joinWithSeparator(' ', phone.prefix, phone.number) || '-';
}

export function formatAssetHolderShares(holder: ViewerAssetHolder): string {
  return joinText(
    holder.fullOwnership !== null && holder.fullOwnership !== undefined
      ? `Pleine propriété ${formatPercentage(holder.fullOwnership)}`
      : null,
    holder.bareOwnership !== null && holder.bareOwnership !== undefined
      ? `Nue-propriété ${formatPercentage(holder.bareOwnership)}`
      : null,
    holder.usufructOwnership !== null && holder.usufructOwnership !== undefined
      ? `Usufruit ${formatPercentage(holder.usufructOwnership)}`
      : null
  ) || '-';
}

export function formatLiabilityHolderShares(holder: ViewerLiabilityHolder): string {
  return joinText(
    holder.ownershipRate !== null && holder.ownershipRate !== undefined
      ? `Taux de détention ${formatPercentage(holder.ownershipRate)}`
      : null,
    holder.adiQuotePart !== null && holder.adiQuotePart !== undefined
      ? `Quote-part ADI ${formatPercentage(holder.adiQuotePart)}`
      : null
  ) || '-';
}

export function formatCoupleRelation(relation: ViewerCoupleRelation): string {
  return joinText(
    relation.relationTypeCode,
    relation.relatedPersonId ? `Personne #${relation.relatedPersonId}` : null
  ) || '-';
}

export function formatFamilyRelation(relation: ViewerFamilyRelation): string {
  return joinText(
    relation.relationTypeCode,
    relation.relatedPersonId ? `Personne #${relation.relatedPersonId}` : null,
    relation.adoptionTypeCode ? `Adoption ${relation.adoptionTypeCode}` : null,
    relation.educationLevelCode ? `Études ${relation.educationLevelCode}` : null
  ) || '-';
}

export function formatOtherRelation(relation: ViewerOtherRelation): string {
  return joinText(
    relation.relationTypeCode,
    relation.relatedPersonId ? `Personne #${relation.relatedPersonId}` : null
  ) || '-';
}

export function formatLiabilityLabel(liability: ViewerLiability): string {
  return joinText(
    liability.designation || `Passif #${liability.liabilityId}`,
    liability.amount !== null && liability.amount !== undefined
      ? formatCurrency(liability.amount)
      : null
  ) || `Passif #${liability.liabilityId}`;
}

export function parseAdditionalFields(json: string | null | undefined): AdditionalField[] {
  if (!json?.trim()) {
    return [];
  }

  try {
    const parsed = JSON.parse(json) as Record<string, { libelle?: unknown; valeur?: unknown }>;

    return Object.values(parsed)
      .map(item => ({
        label: typeof item?.libelle === 'string' ? item.libelle : '',
        value: item?.valeur === null || item?.valeur === undefined ? '-' : String(item.valeur)
      }))
      .filter(item => item.label);
  } catch {
    return [];
  }
}

export function getTaxAdditionalFields(tax: ViewerTax): AdditionalField[] {
  return [
    ...parseAdditionalFields(tax.incomeTaxAdditionalFieldsJson),
    ...parseAdditionalFields(tax.socialContributionsAdditionalFieldsJson),
    ...parseAdditionalFields(tax.wealthTaxAdditionalFieldsJson)
  ];
}

export function getAssetLinkedLiabilities(
  viewer: InvestorViewerData,
  assetId: number
): ViewerLiability[] {
  return viewer.liabilities.filter(liability => liability.linkedAssetId === assetId);
}

export function isChargeItem(item: ViewerRevenueCharge): boolean {
  return (item.typeCode ?? '').toUpperCase() === 'CHARGES';
}

function joinText(...values: Array<string | null | undefined>): string {
  return joinWithSeparator(' • ', ...values);
}

function joinWithSeparator(separator: string, ...values: Array<string | null | undefined>): string {
  return values
    .map(value => value?.trim())
    .filter((value): value is string => !!value)
    .join(separator);
}
